import { z } from "zod/v4";

import { ShopifyTemplateType } from "./shopify";

export const RedirectQueryParamDtoSchema = z.object({
  key: z.string().describe("Query parameter name"),
  value: z.string().describe("Query parameter value"),
});

export type RedirectQueryParamDto = z.infer<typeof RedirectQueryParamDtoSchema>;

export const RedirectFilterList = [
  "matchesExactly",
  "contains",
  "endsWith",
  "matchesRegex",
  "none",
] as const;
export type RedirectFilter = (typeof RedirectFilterList)[number];

export const UrlRedirectTypeList = [
  "fullReplacement",
  "partialReplacement",
] as const;
export type UrlRedirectType = (typeof UrlRedirectTypeList)[number];

export const RedirectDtoSchema = z.object({
  id: z.uuid().describe("Unique identifier for the redirect"),
  group: z
    .number()
    .describe("Redirect group number for organizing related redirects"),
  variationId: z.uuid().describe("Variation this redirect belongs to"),
  originUrl: z
    .string()
    .describe("URL pattern to match for redirect (supports wildcards)"),
  destinationUrl: z.string().nullish().describe("URL to redirect users to"),
  queryParams: RedirectQueryParamDtoSchema.array()
    .default([])
    .describe("Query parameters to add to the destination URL"),
  skip: z.boolean().describe("Whether to skip this redirect"),
  isThemeTest: z.boolean().describe("Whether this is testing different themes"),
  isTemplateTest: z
    .boolean()
    .describe("Whether this is testing different templates"),
  templateType: z
    .enum(ShopifyTemplateType)
    .nullish()
    .describe("Type of Shopify template being tested"),
  templateSuffixes: z
    .string()
    .array()
    .nullish()
    .default([])
    .describe("Template suffix variations to test"),
  redirectOnce: z
    .boolean()
    .default(false)
    .describe("Whether to only redirect once per user session"),
  filter: z
    .enum(RedirectFilterList)
    .default("matchesExactly")
    .describe(
      "How to match URLs: 'matchesExactly', 'contains', 'endsWith', 'matchesRegex', 'none'"
    ),
  find: z
    .string()
    .nullish()
    .describe("Text to find in the URL for partial matching"),
  redirectType: z
    .enum(UrlRedirectTypeList)
    .default("fullReplacement")
    .describe(
      "Whether to do 'fullReplacement' or 'partialReplacement' of the URL"
    ),
});

export type RedirectDto = z.infer<typeof RedirectDtoSchema>;
